// ==========================================================================
// くちびるLABO Modal - Detail modal with slide navigation
// ==========================================================================

import $ from 'jquery';
import { laboCards } from '../../data/labo-cards-data.js';
import { colorProducts } from '../../data/color-products-data.js';
import { medicatedProducts } from '../../data/medicated-products-data.js';

const ANIMATION_MS = 350;

class LaboModal {
	constructor() {
		this.$modal = $('#modal-labo');
		if (!this.$modal.length) return;

		this.$backdrop = this.$modal.find('.modal__backdrop');
		this.$slides = this.$modal.find('.modal-labo__slide');
		this.$prevBtn = this.$modal.find('.modal-labo__nav--prev');
		this.$nextBtn = this.$modal.find('.modal-labo__nav--next');

		this.currentIndex = 0;
		this.isAnimating = false;
		this.previousScrollY = 0;

		this.init();
	}

	init() {
		// おすすめリップを事前レンダリング
		laboCards.forEach((card) => {
			this.renderRecommend(card);
		});

		// 閉じるボタン
		this.$modal.on('click', '.modal__close', () => this.close());

		// 背景クリック
		this.$backdrop.on('click', () => this.close());

		// ESCキー
		$(document).on('keydown.laboModal', (e) => {
			if (e.key === 'Escape' && this.$modal.hasClass('is-active')) {
				this.close();
			}
		});

		// 前後ナビ
		this.$prevBtn.on('click', () => this.navigate(-1));
		this.$nextBtn.on('click', () => this.navigate(1));

		// 店舗一覧ボタン
		this.$modal.on('click', '.modal-labo__recommend-stores', (e) => {
			e.preventDefault();
			this.handleStoresClick();
		});
	}

	open(modalId) {
		const index = laboCards.findIndex((c) => c.modalId === modalId);
		if (index < 0) {
			console.warn('[labo-modal] Unknown modalId:', modalId);
			return;
		}

		this.currentIndex = index;

		// アクティブスライドを設定
		this.$slides.removeClass('is-active is-leaving-left is-leaving-right is-entering-left is-entering-right');
		const $target = this.$slides.eq(index);
		$target.addClass('is-active');
		this.applyModalColor($target);
		this.updateNavAvailability();

		// スクロール位置リセット
		$target.find('.modal-labo__inner').scrollTop(0);

		// 背景スクロール抑止（scrollY保持）
		this.previousScrollY = window.scrollY;
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this.previousScrollY}px`;
		document.body.style.width = '100%';

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.$modal.addClass('is-active');
			});
		});
	}

	close() {
		this.$modal.removeClass('is-active');

		setTimeout(() => {
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			window.scrollTo(0, this.previousScrollY);

			this.$slides.removeClass('is-active is-leaving-left is-leaving-right is-entering-left is-entering-right');
		}, 300);
	}

	navigate(direction) {
		if (this.isAnimating) return;

		const nextIndex = (this.currentIndex + direction + laboCards.length) % laboCards.length;
		if (nextIndex === this.currentIndex) return;

		this.isAnimating = true;

		const $current = this.$slides.eq(this.currentIndex);
		const $next = this.$slides.eq(nextIndex);

		const leavingClass = direction > 0 ? 'is-leaving-left' : 'is-leaving-right';
		const enteringClass = direction > 0 ? 'is-entering-right' : 'is-entering-left';

		// 次スライドを初期位置に配置
		$next.removeClass('is-active').addClass(enteringClass);
		this.applyModalColor($next);

		// 強制リフロー
		void $next[0].offsetHeight;

		// トランジション開始
		$current.removeClass('is-active').addClass(leavingClass);
		$next.removeClass(enteringClass).addClass('is-active');

		this.currentIndex = nextIndex;
		this.updateNavAvailability();

		// スクロール位置リセット
		$next.find('.modal-labo__inner').scrollTop(0);

		setTimeout(() => {
			$current.removeClass(leavingClass);
			this.isAnimating = false;
		}, ANIMATION_MS);
	}

	updateNavAvailability() {
		// 循環ナビゲーションのため無効化はしない（Figmaデザインでも常時表示）
		// 必要なら以下を有効化:
		// this.$prevBtn.prop('disabled', this.currentIndex === 0);
		// this.$nextBtn.prop('disabled', this.currentIndex === laboCards.length - 1);
	}

	applyModalColor($slide) {
		const color = $slide.data('modal-color');
		if (color) {
			$slide[0].style.setProperty('--modal-labo-bg', color);
		}
	}

	// ==========================================================================
	// おすすめリップセクション描画
	// ==========================================================================

	renderRecommend(card) {
		if (!card.recommend) return;

		const $slide = this.$modal.find(`[data-slide-id="${card.id}"]`);
		const $recommendContainer = $slide.find('.modal-labo__recommend');
		if (!$recommendContainer.length) return;

		const { heading, groups } = card.recommend;
		if (!groups || !groups.length) return;

		const groupsHtml = groups.map((g) => this.renderGroup(g)).join('');
		const html = `
			<p class="modal-labo__recommend-heading">このお悩みにおすすめの<br class="d-md-none">DHCのリップクリーム</p>
			${groupsHtml}
		`;
		$recommendContainer.html(html);
	}

	renderGroup(group) {
		const source = group.series === 'color' ? colorProducts : medicatedProducts;
		const items = (group.products || []).map((entry) => {
			const product = source.find((p) => p.id === entry.id);
			return product ? { product, thumb: entry.thumb } : null;
		}).filter(Boolean);

		const productsHtml = items.map(({ product, thumb }) => this.renderProductCard(product, group.series, thumb)).join('');
		const badgeClass = group.series === 'color' ? 'modal-labo__recommend-badge--color' : '';
		const badgeText = group.series === 'color' ? 'DHCのカラーリップシリーズ' : 'DHCのリップシリーズ';

		return `
			<div class="modal-labo__recommend-group">
				<div class="modal-labo__recommend-tag">
					<span class="modal-labo__recommend-tag-icon">
						<img src="./assets/images/labo/i_check.svg" alt="" loading="lazy">
					</span>
					<p class="modal-labo__recommend-tag-text">${this.escapeHtml(group.label)}</p>
				</div>
				<span class="modal-labo__recommend-badge ${badgeClass}">${this.escapeHtml(badgeText)}</span>
				<div class="modal-labo__recommend-products">
					${productsHtml}
				</div>
				<button class="modal-labo__recommend-stores" type="button">取り扱い店舗一覧はこちら</button>
			</div>
		`;
	}

	renderProductCard(product, series, thumbSrc) {
		const isRound = series === 'color';
		const plainName = this.stripHtmlTags(product.name);
		const hasTag = !!product.tag;
		return `
			<div class="modal-labo__recommend-product">
				<span class="modal-labo__recommend-product-thumb ${isRound ? 'modal-labo__recommend-product-thumb--round' : ''}">
					<img src="${this.escapeAttr(thumbSrc)}" alt="${this.escapeAttr(plainName)}" loading="lazy">
				</span>
				<p class="modal-labo__recommend-product-name">${this.wrapWordsInline(product.name)}</p>
				${hasTag ? `<span class="modal-labo__recommend-product-tag">${this.escapeHtml(product.tag)}</span>` : ''}
			</div>
		`;
	}

	// ==========================================================================
	// 店舗スクロール連携
	// ==========================================================================

	handleStoresClick() {
		this.close();

		setTimeout(() => {
			const target = document.getElementById('stores');
			if (!target) return;
			const header = document.querySelector('.header');
			const headerH = header ? header.offsetHeight : 0;
			const y = target.getBoundingClientRect().top + window.scrollY - headerH - 20;
			window.scrollTo({ top: y, behavior: 'smooth' });
		}, 320);
	}

	// ==========================================================================
	// ヘルパ
	// ==========================================================================

	stripHtmlTags(str) {
		if (!str) return '';
		return String(str).replace(/<[^>]*>/g, '');
	}

	// 半角スペース区切りで各セグメントを inline-block の span で包み、単語途中で改行させない
	// 元テキスト内の <br> は空白として扱い、単語境界として機能させる
	wrapWordsInline(str) {
		if (!str) return '';
		const normalized = String(str)
			.replace(/<br\b[^>]*>/gi, ' ') // <br ...> は空白扱い（属性付きも含む）
			.replace(/<[^>]*>/g, ''); // その他のタグは除去
		const parts = normalized.split(/\s+/).filter(Boolean);
		return parts
			.map((part) => `<span class="modal-labo__recommend-product-name-part">${this.escapeHtml(part)}</span>`)
			.join(' ');
	}

	escapeHtml(str) {
		if (str === null || str === undefined) return '';
		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
	}

	escapeAttr(str) {
		if (str === null || str === undefined) return '';
		return String(str).replace(/"/g, '&quot;');
	}
}

// シングルトンでインスタンス化し、外部から open できるようにする
let instance = null;

$(() => {
	instance = new LaboModal();

	// labo.js から呼び出せるようグローバル露出
	window.openLaboModal = (modalId) => {
		if (instance) instance.open(modalId);
	};
});

export default LaboModal;
